import {tx, ptx, usePageText} from '../../lib/page-text';
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api-client";
import type { Program } from "../../../../api/src/live-program/program-schema";
import type { WhipPublisher } from "../WhipPublisher";
export function useProgramPermissions(streamId?: string) {
  return useQuery({
    queryKey: ["program", streamId],
    enabled: !!streamId,
    queryFn: async () =>
      (
        await api.get<{
          version: number;
          config: Program;
          permissions: { board: boolean; produce: boolean; moderate: boolean };
        }>(`/me/streams/${streamId}/program`)
      ).data,
    refetchInterval: 10000,
    retry: false,
  });
}
export function ProducerPanel({
  streamId,
  pub,
}: {
  streamId: string;
  pub: WhipPublisher;
}) {
  usePageText();
  const q = useProgramPermissions(streamId),
    prefix = `/me/streams/${streamId}/program`,
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false),
    [answers, setAnswers] = useState<Record<string, string>>({});
  const stats = useRef(pub.stats);
  stats.current = pub.stats;
  useEffect(() => {
    if (pub.state !== "live" || !q.data?.config.features.production) return;
    const timer = setInterval(() => {
      void api.post(prefix + "/health", stats.current).catch(() => {});
    }, 15000);
    return () => clearInterval(timer);
  }, [pub.state, q.data?.config.features.production, prefix]);
  const health = useQuery({
    queryKey: ["program-health", streamId],
    enabled: !!q.data?.config.features.production,
    queryFn: async () =>
      (
        await api.get<{
          status: string;
          latest: ({ at: string } & WhipPublisher["stats"]) | null;
        }>(prefix + "/health")
      ).data,
    refetchInterval: 15000,
  });
  const moderation = useQuery({
    queryKey: ["program-moderation", streamId],
    enabled:
      !!q.data?.config.features.production && !!q.data?.permissions.moderate,
    queryFn: async () =>
      (
        await api.get<{
          questions: { id: string; body: string; askerName?: string }[];
        }>(prefix + "/moderation")
      ).data,
    refetchInterval: 5000,
  });
  async function command(path: string, body: unknown) {
    setBusy(true);
    try {
      await api.patch(prefix + path, body);
      await q.refetch();
      setNotice("Updated");
    } catch (e: any) {
      setNotice(e?.response?.data?.message ?? "Could not update. Retry.");
    } finally {
      setBusy(false);
    }
  }
  if (!q.data) return null;
  const p = q.data.config,
    rights = q.data.permissions;
  if (!p.features.production) return null;
  return (
    <details className="card live-program producer-panel">
      <summary>{tx("Producer desk")}</summary>
      <p role="status">{notice}</p>
      {p.features.production && (
        <>
          <h3>{tx("Broadcast health")}</h3>
          {pub.state === "live" ? (
            <p>{ptx("{v0} · {v1} kbps ·{v2}{v3} ms round-trip", {v0: (pub.stats.resolution), v1: (pub.stats.kbps), v2: (" "), v3: (pub.stats.rtt ?? "—")})}</p>
          ) : health.data?.latest &&
            Date.now() - Date.parse(health.data.latest.at) < 45000 ? (
            <p>{ptx("{v0} · {v1} kbps ·{v2}{v3} ms · Updated{v4}{v5}", {v0: (health.data.latest.resolution), v1: (health.data.latest.kbps), v2: (" "), v3: (health.data.latest.rtt ?? "—"), v4: (" "), v5: (new Date(health.data.latest.at).toLocaleTimeString())})}</p>
          ) : (
            <p className="muted">{tx("No recent browser publisher telemetry. External encoders do not report through this panel.")}</p>
          )}
          <p className="muted">{tx("Older health samples are removed as new samples arrive. A low bitrate alone does not identify the cause of a connection problem.")}</p>
        </>
      )}
      {rights.produce && (
        <>
          <div className="program-row">
            {p.features.actions && (
              <label>{ptx("Audience action card{v0}", {v0: (<select
                  className="input"
                  disabled={busy}
                  value={p.activeCardId ?? ""}
                  onChange={(e) =>
                    void command("/control", {
                      activeCardId: e.target.value || null,
                    })
                  }
                >
                  <option value="">{tx("No card")}</option>
                  {p.cards.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>)})}</label>
            )}
          </div>
          {p.features.teaching &&
            p.quizzes.map((quiz) => (
              <div key={quiz.id} className="program-row">
                <strong>{quiz.title}</strong>
                <button
                  className="btn outline sm"
                  disabled={busy}
                  onClick={() =>
                    void command("/control", {
                      quiz: { id: quiz.id, open: !quiz.open },
                    })
                  }
                >
                  {quiz.open ? tx("Close quiz") : tx("Open quiz")}
                </button>
              </div>
            ))}
          {p.features.production &&
            p.cues.map((c) => (
              <label key={c.id}>
                <input
                  type="checkbox"
                  checked={c.done}
                  disabled={busy}
                  onChange={(e) =>
                    void command("/control", {
                      cue: { id: c.id, done: e.target.checked },
                    })
                  }
                />
                {c.title} · {c.minutes}m{" "}
                <small className="muted">{c.notes}</small>
              </label>
            ))}
        </>
      )}
      {rights.moderate && (
        <>
          <h3>{tx("Question queue")}</h3>
          {moderation.isError ? (
            <button
              className="btn outline sm"
              onClick={() => void moderation.refetch()}
            >{tx("Retry question queue")}</button>
          ) : !moderation.data?.questions.length ? (
            <p className="muted">{tx("No unanswered questions.")}</p>
          ) : (
            moderation.data.questions.map((question) => (
              <article className="program-item" key={question.id}>
                <strong>{question.body}</strong>
                <p className="muted">{question.askerName ?? tx("Viewer")}</p>
                <label>{ptx("Written answer{v0}", {v0: (<textarea
                    className="input"
                    value={answers[question.id] ?? ""}
                    onChange={(e) =>
                      setAnswers({ ...answers, [question.id]: e.target.value })
                    }
                  />)})}</label>
                <div className="program-row">
                  {[
                    ["live", "Show question"],
                    ["answered", "Send answer"],
                    ["dismissed", "Dismiss"],
                  ].map(([status, label]) => (
                    <button
                      className="btn outline sm"
                      key={status}
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          await api.post(prefix + "/moderation", {
                            id: question.id,
                            status,
                            answer: answers[question.id] ?? "",
                          });
                          await moderation.refetch();
                        } catch (e: any) {
                          setNotice(
                            e?.response?.data?.message ??
                              "Could not moderate question.",
                          );
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </article>
            ))
          )}
        </>
      )}
    </details>
  );
}
